import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PageWrapper, Card, Button } from "../components";
import MicIframeNotice from "../components/MicIframeNotice";
import { 
  ArrowLeft, Mic, Square, Play, Pause, RotateCcw,
  Image as ImageIcon, Share2, Download, Check, Heart
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Prompt suggestions for Send a Moment
const PROMPT_SUGGESTIONS = [
  "Thinking of you today",
  "Take a breath with me",
  "You've got this",
  "A calm moment for you"
];

// Background images for moment cards
const GIFT_BACKGROUNDS = [
  {
    id: "sunrise",
    name: "Warm Sunrise",
    url: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=800&q=80",
    overlay: "rgba(180, 120, 80, 0.4)"
  },
  {
    id: "flowers",
    name: "Spring Flowers",
    url: "https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=800&q=80",
    overlay: "rgba(150, 100, 120, 0.4)"
  },
  {
    id: "ocean",
    name: "Calm Ocean",
    url: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=80",
    overlay: "rgba(60, 100, 140, 0.4)"
  },
  {
    id: "forest",
    name: "Peaceful Forest",
    url: "https://images.unsplash.com/photo-1448375240586-882707db888b?w=800&q=80",
    overlay: "rgba(50, 80, 50, 0.5)"
  },
  {
    id: "stars",
    name: "Starry Night",
    url: "https://images.unsplash.com/photo-1519681393784-d120267933ba?w=800&q=80",
    overlay: "rgba(30, 40, 80, 0.5)"
  }
];

export const GiftCreatePage = () => {
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  // Steps: 1=Write, 2=Record, 3=Background, 4=Preview, 5=Share
  const [step, setStep] = useState(1);
  const [message, setMessage] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [selectedBackground, setSelectedBackground] = useState(GIFT_BACKGROUNDS[0]);
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const timerRef = useRef(null);

  // Generated gift
  const [giftId, setGiftId] = useState(null);
  const [saving, setSaving] = useState(false);

  const getToken = () => localStorage.getItem("reclaim_token");

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Determine best supported MIME type for the browser
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/aac')) {
        mimeType = 'audio/aac';
      } else if (MediaRecorder.isTypeSupported('audio/wav')) {
        mimeType = 'audio/wav';
      } else {
        // Fallback - let browser choose
        mimeType = '';
      }
      
      const options = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const actualMimeType = mediaRecorder.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: actualMimeType });
        setRecordedBlob(blob);
        setRecordedUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start(100); // Capture in 100ms chunks for better compatibility
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (err) {
      console.error("Recording error:", err);
      if (err.name === 'NotAllowedError') {
        toast.error("Please allow microphone access in your browser settings");
      } else if (err.name === 'NotFoundError') {
        toast.error("No microphone found on this device");
      } else {
        toast.error("Could not access microphone. Please try again.");
      }
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  // Play/pause recorded audio
  const togglePlayback = () => {
    if (!audioRef.current || !recordedUrl) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.src = recordedUrl;
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  // Re-record
  const handleReRecord = () => {
    setRecordedBlob(null);
    setRecordedUrl(null);
    setRecordingTime(0);
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Save gift to backend
  const saveGift = async () => {
    if (!recordedBlob || !message.trim()) {
      toast.error("Please record a message first");
      return;
    }

    setSaving(true);
    try {
      // Convert blob to base64
      const reader = new FileReader();
      const audioBase64 = await new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(recordedBlob);
      });

      const response = await fetch(`${API_URL}/api/voice-studio/gifts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          message: message.trim(),
          recipient_name: recipientName.trim() || null,
          background_id: selectedBackground.id,
          audio_data: audioBase64
        })
      });

      if (!response.ok) {
        throw new Error("Failed to save gift");
      }

      const data = await response.json();
      setGiftId(data.id);
      setStep(5);
      toast.success("Gift created!");
    } catch (err) {
      console.error("Error saving gift:", err);
      toast.error("Failed to create gift");
    } finally {
      setSaving(false);
    }
  };

  // Share gift
  const handleShare = async () => {
    const giftUrl = `${window.location.origin}/gift/${giftId}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: "A Gift for You - Reclaim",
          text: recipientName ? `${recipientName}, I recorded a special message for you.` : "I recorded a special message for you.",
          url: giftUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          navigator.clipboard.writeText(giftUrl);
          toast.success("Link copied to clipboard!");
        }
      }
    } else {
      navigator.clipboard.writeText(giftUrl);
      toast.success("Link copied to clipboard!");
    }
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    };
  }, [recordedUrl]);

  const canProceed = () => {
    switch (step) {
      case 1: return message.trim().length > 0;
      case 2: return recordedBlob !== null;
      case 3: return selectedBackground !== null;
      case 4: return true;
      default: return false;
    }
  };

  return (
    <PageWrapper hideNav>
      <div className="min-h-screen bg-[#FAFAF7] pb-8">
        {/* Header */}
        <div className="bg-gradient-to-b from-[#D4A373] to-[#C4956A] text-white px-6 pt-8 pb-12">
          <div className="max-w-lg mx-auto">
            <button 
              onClick={() => step > 1 ? setStep(step - 1) : navigate("/voice-studio?tab=moment")}
              className="flex items-center gap-2 text-white/80 hover:text-white mb-4"
            >
              <ArrowLeft size={20} />
              <span>Back</span>
            </button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Heart size={24} />
              </div>
              <div>
                <h1 className="font-heading text-2xl">Send a Moment</h1>
                <p className="text-white/70 text-sm">Step {step} of 5</p>
              </div>
            </div>
            
            {/* Progress */}
            <div className="flex gap-2 mt-6">
              {[1, 2, 3, 4, 5].map(s => (
                <div
                  key={s}
                  className={`h-1.5 flex-1 rounded-full transition-colors ${
                    s <= step ? "bg-white" : "bg-white/30"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 -mt-6 max-w-lg mx-auto">
          {/* Step 1: Write Message */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="p-6">
                <h2 className="font-heading text-xl text-[#1F2937] mb-2">Send a Moment</h2>
                <p className="text-sm text-[#6B7280] mb-4">
                  What would you like to say? Keep it short and heartfelt.
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-[#6B7280] block mb-1">
                      Recipient's Name (optional)
                    </label>
                    <input
                      type="text"
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                      placeholder="e.g., Sarah"
                      className="w-full px-4 py-3 border border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4A373] text-[#1F2937]"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-[#6B7280] block mb-1">
                      Your Message
                    </label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Type your message or use a prompt below..."
                      rows={4}
                      maxLength={200}
                      className="w-full px-4 py-3 border border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4A373] text-[#1F2937] resize-none"
                    />
                    <p className="text-xs text-[#9CA3AF] text-right mt-1">
                      {message.length}/200
                    </p>
                  </div>

                  {/* Prompt Suggestions */}
                  <div>
                    <p className="text-xs text-[#9CA3AF] mb-2">Need inspiration?</p>
                    <div className="flex flex-wrap gap-2">
                      {PROMPT_SUGGESTIONS.map((prompt, idx) => (
                        <button
                          key={idx}
                          onClick={() => setMessage(prompt)}
                          className="text-xs px-3 py-1.5 bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#6B7280] rounded-full transition-colors"
                          data-testid={`prompt-suggestion-${idx}`}
                        >
                          {prompt}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <Button
                  onClick={() => setStep(2)}
                  disabled={!canProceed()}
                  className="w-full mt-6"
                >
                  Continue to Record
                </Button>
              </Card>
            </motion.div>
          )}

          {/* Step 2: Record */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="p-6">
                <h2 className="font-heading text-xl text-[#1F2937] mb-2">Record Your Message</h2>
                <p className="text-sm text-[#6B7280] mb-6">
                  Read your message aloud with feeling. Speak slowly and clearly.
                </p>

                <MicIframeNotice className="mb-6" accent="#D4A373" theme="light" />

                {/* Message Preview */}
                <div className="bg-[#F9FAFB] rounded-xl p-4 mb-6">
                  {recipientName && (
                    <p className="text-sm text-[#D4A373] font-medium mb-1">To: {recipientName}</p>
                  )}
                  <p className="text-[#1F2937] italic">"{message}"</p>
                </div>

                {/* Recording UI */}
                <div className="text-center">
                  {!recordedBlob ? (
                    <>
                      <div className="text-3xl font-mono text-[#1F2937] mb-4">
                        {formatTime(recordingTime)}
                      </div>
                      
                      <button
                        onClick={isRecording ? stopRecording : startRecording}
                        className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto transition-all ${
                          isRecording 
                            ? "bg-red-500 text-white animate-pulse" 
                            : "bg-[#D4A373] text-white hover:bg-[#C4956A]"
                        }`}
                        data-testid="record-gift-btn"
                      >
                        {isRecording ? <Square size={32} /> : <Mic size={32} />}
                      </button>
                      
                      <p className="text-sm text-[#6B7280] mt-4">
                        {isRecording ? "Tap to stop" : "Tap to start recording"}
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center justify-center gap-4 mb-6">
                        <button
                          onClick={togglePlayback}
                          className="w-16 h-16 bg-[#D4A373] text-white rounded-full flex items-center justify-center"
                        >
                          {isPlaying ? <Pause size={28} /> : <Play size={28} className="ml-1" />}
                        </button>
                        <button
                          onClick={handleReRecord}
                          className="w-12 h-12 bg-[#F3F4F6] text-[#6B7280] rounded-full flex items-center justify-center hover:bg-[#E5E7EB]"
                        >
                          <RotateCcw size={20} />
                        </button>
                      </div>
                      <p className="text-sm text-[#6B7280]">
                        Duration: {formatTime(recordingTime)}
                      </p>
                    </>
                  )}
                </div>

                <Button
                  onClick={() => setStep(3)}
                  disabled={!canProceed()}
                  className="w-full mt-6"
                >
                  Continue
                </Button>
              </Card>
            </motion.div>
          )}

          {/* Step 3: Choose Background */}
          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="p-6">
                <h2 className="font-heading text-xl text-[#1F2937] mb-2">Choose a Background</h2>
                <p className="text-sm text-[#6B7280] mb-6">
                  Select an image for your gift card.
                </p>

                <div className="grid grid-cols-2 gap-3">
                  {GIFT_BACKGROUNDS.map(bg => (
                    <button
                      key={bg.id}
                      onClick={() => setSelectedBackground(bg)}
                      className={`relative aspect-[4/3] rounded-xl overflow-hidden transition-all ${
                        selectedBackground.id === bg.id
                          ? "ring-4 ring-[#D4A373] ring-offset-2"
                          : "hover:scale-105"
                      }`}
                    >
                      <img
                        src={bg.url}
                        alt={bg.name}
                        className="w-full h-full object-cover"
                      />
                      {selectedBackground.id === bg.id && (
                        <div className="absolute inset-0 bg-[#D4A373]/30 flex items-center justify-center">
                          <Check size={32} className="text-white" />
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                        <p className="text-white text-xs">{bg.name}</p>
                      </div>
                    </button>
                  ))}
                </div>

                <Button
                  onClick={() => setStep(4)}
                  className="w-full mt-6"
                >
                  Preview Gift
                </Button>
              </Card>
            </motion.div>
          )}

          {/* Step 4: Preview */}
          {step === 4 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="overflow-hidden">
                {/* Gift Card Preview */}
                <div 
                  className="relative aspect-square"
                  style={{
                    backgroundImage: `url(${selectedBackground.url})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div 
                    className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center"
                    style={{ backgroundColor: selectedBackground.overlay }}
                  >
                    <Heart size={32} className="text-white/80 mb-4" />
                    {recipientName && (
                      <p className="text-white/80 text-sm mb-2">For {recipientName}</p>
                    )}
                    <p className="text-white font-heading text-xl italic leading-relaxed">
                      "{message}"
                    </p>
                    <p className="text-white/60 text-sm mt-6">
                      Tap to play • Reclaim
                    </p>
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  <h3 className="font-heading text-lg text-[#1F2937] text-center">
                    Your Moment is Ready
                  </h3>
                  <p className="text-sm text-[#6B7280] text-center">
                    When they open the link, they'll see this card and hear your voice.
                  </p>

                  <Button
                    onClick={saveGift}
                    disabled={saving}
                    className="w-full"
                  >
                    {saving ? "Creating..." : "Send a Moment"}
                  </Button>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Step 5: Share */}
          {step === 5 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="p-6 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-[#4A6741] to-[#3D5A34] rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check size={40} className="text-white" />
                </div>

                <h2 className="font-heading text-2xl text-[#1F2937] mb-2">Moment Created!</h2>
                <p className="text-sm text-[#6B7280] mb-6">
                  Share the link with {recipientName || "your recipient"} so they can listen to your message.
                </p>

                <div className="space-y-3">
                  <Button onClick={handleShare} className="w-full">
                    <Share2 size={18} />
                    Share Moment
                  </Button>

                  <Button 
                    variant="outline" 
                    onClick={() => navigate("/voice-studio?tab=moment")}
                    className="w-full"
                  >
                    Back to Studio
                  </Button>
                </div>
              </Card>
            </motion.div>
          )}
        </div>

        {/* Hidden audio element */}
        <audio 
          ref={audioRef} 
          onEnded={() => setIsPlaying(false)}
        />
      </div>
    </PageWrapper>
  );
};

export default GiftCreatePage;
